<template>
  <div class="forum-wrapper">
    <div class="forum-layout">
      <!-- Main Column -->
      <div class="main-column">
        <!-- Form Header -->
        <header class="form-header">
          <h1 class="typo-headline-4">Crear Nuevo Hilo</h1>
        </header>

        <!-- Loading and Error States -->
        <div v-if="loading" class="loading-message typo-body-2">
          Cargando formulario...
        </div>
        <div v-else-if="error" class="error-message typo-body-2">
          {{ error }}
        </div>

        <!-- Thread Form -->
        <div v-else class="thread-form-container">
          <form @submit.prevent="submitThread" class="thread-form">
            <!-- Section Selector -->
            <div class="form-group">
              <label for="thread-section" class="typo-subtitle-1">Sección</label>
              <select 
                id="thread-section" 
                v-model="selectedSectionSlug" 
                class="form-control typo-body-1"
                required
              >
                <option value="" disabled>Selecciona una sección</option>
                <optgroup v-if="mainSections.length" label="Área Principal">
                  <option 
                    v-for="section in mainSections" 
                    :key="section.id" 
                    :value="section.slug"
                  >
                    {{ section.title }}
                  </option>
                </optgroup>
                <optgroup v-if="secondarySections.length" label="Área Web">
                  <option 
                    v-for="section in secondarySections" 
                    :key="section.id" 
                    :value="section.slug"
                  >
                    {{ section.title }}
                  </option>
                </optgroup>
              </select>
            </div>

            <div class="form-group">
              <label for="thread-title" class="typo-subtitle-1">Título del Hilo</label>
              <input 
                type="text" 
                id="thread-title" 
                v-model="threadTitle" 
                class="form-control typo-body-1"
                placeholder="Escribe un título descriptivo"
                required
              />
            </div>

            <div class="form-group">
              <label for="thread-content" class="typo-subtitle-1">Contenido</label>
              <wysiwyg-editor
                v-model="threadContent"
                name="thread-content"
                placeholder="Escribe el contenido de tu hilo"
                ref="wysiwygEditor"
              />
            </div>

            <div class="form-actions">
              <button type="button" class="cancel-btn typo-button" @click="cancelThread">
                Cancelar
              </button>
              <button type="submit" class="submit-btn typo-button" :disabled="submitting">
                <span v-if="submitting">Enviando...</span>
                <span v-else>Publicar Hilo</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Side Column -->
      <div class="side-column">
        <!-- Navigation Links -->
        <navigation-links 
          v-if="selectedSectionSlug && selectedSectionTitle"
          :section-name="selectedSectionTitle"
          :section-slug="selectedSectionSlug"
        />

        <!-- Forum Guidelines -->
        <div class="forum-guidelines">
          <h3 class="typo-headline-6">Normas del Foro</h3>
          <div class="guidelines-content typo-body-2">
            <p>Antes de publicar, recuerda:</p>
            <ul>
              <li>Sé respetuoso con otros usuarios</li>
              <li>Evita el contenido ofensivo o inapropiado</li>
              <li>Usa un título descriptivo para tu hilo</li>
              <li>Revisa si tu pregunta ya ha sido respondida</li>
              <li>Proporciona información relevante y clara</li>
            </ul>
            <router-link to="/forum/rules" class="rules-link">
              Ver todas las normas
            </router-link>
          </div>
        </div>

        <!-- Formatting Tips -->
        <div class="formatting-tips">
          <h3 class="typo-headline-6">Consejos de Formato</h3>
          <div class="tips-content typo-body-2">
            <p>Puedes usar los botones de la barra de herramientas para:</p>
            <ul>
              <li><strong>Negrita</strong> para destacar texto importante</li>
              <li><em>Cursiva</em> para enfatizar palabras</li>
              <li>Crear listas ordenadas y con viñetas</li>
              <li>Añadir enlaces a recursos externos</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
import NavigationLinks from '../components/NavigationLinks.vue';
import WysiwygEditor from '@/components/WysiwygEditor.vue';

const axios = forumApi;

export default {
  name: 'NewThreadForm',
  components: {
    NavigationLinks,
    WysiwygEditor
  },
  
  data() {
    return {
      selectedSectionSlug: '',
      selectedSectionTitle: '',
      threadTitle: '',
      threadContent: '',
      loading: true,
      submitting: false,
      error: null,
      mainSections: [],
      secondarySections: [],
      sectionIdMap: {} // Map to store slug to ID mapping
    };
  },
  
  created() {
    // Check if a section slug was provided in the query string
    const sectionSlug = this.$route.query.sectionSlug;
    if (sectionSlug) {
      this.selectedSectionSlug = sectionSlug;
    }
    
    // Fetch all available sections
    this.fetchForumSections();
  },
  
  computed: {
    // Get the section ID from the selected slug
    selectedSectionId() {
      return this.sectionIdMap[this.selectedSectionSlug] || '';
    },
    
    // Check if form is valid
    isFormValid() {
      return this.selectedSectionId && 
             this.threadTitle.trim() && 
             this.threadContent.trim();
    }
  },
  
  methods: {
    fetchForumSections() {
      this.loading = true;
      
      axios.get('/api/forum-sections')
        .then(response => {
          if (response.data && response.data.success) {
            this.mainSections = response.data.sections.main || [];
            this.secondarySections = response.data.sections.secondary || [];
            
            // Create a mapping of slug to ID
            this.createSectionIdMap();
            
            this.error = null;
          } else {
            throw new Error('Invalid response format');
          }
        })
        .catch(error => {
          console.error('Error fetching forum sections:', error);
          
          // Fallback to hardcoded data if API fails
          this.mainSections = [
            { 
              id: 256, 
              slug: 'discusion-general',
              title: 'Discusión General', 
              description: 'Espacio para hablar de temas generales relacionados con la comunidad.'
            },
            { 
              id: 2, 
              slug: 'foro-valores',
              title: 'Foro de Valores', 
              description: 'Discusiones sobre valores, acciones y mercados financieros.'
            }
          ];
          
          this.secondarySections = [
            { 
              id: 3, 
              slug: 'novedades',
              title: 'Novedades', 
              description: 'Anuncios y novedades sobre la plataforma y la comunidad.'
            },
            { 
              id: 4, 
              slug: 'sugerencias',
              title: 'Sugerencias', 
              description: 'Propón mejoras y nuevas funcionalidades para la plataforma.'
            },
            { 
              id: 5, 
              slug: 'dudas',
              title: 'Dudas', 
              description: 'Resuelve tus dudas sobre el funcionamiento de la plataforma.'
            },
            { 
              id: 6, 
              slug: 'peticion-valores',
              title: 'Petición de Valores', 
              description: 'Solicita la inclusión de nuevos valores en la plataforma.'
            },
            { 
              id: 7, 
              slug: 'errores',
              title: 'Errores', 
              description: 'Reporta errores o problemas técnicos que hayas encontrado.'
            }
          ];
          
          // Create a mapping of slug to ID
          this.createSectionIdMap();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    createSectionIdMap() {
      // Reset the map
      this.sectionIdMap = {};
      
      // Add main sections to the map
      this.mainSections.forEach(section => {
        if (section.slug) {
          this.sectionIdMap[section.slug] = section.id;
          // Also store the title for the selected section
          if (section.slug === this.selectedSectionSlug) {
            this.selectedSectionTitle = section.title;
          }
        }
      });
      
      // Add secondary sections to the map
      this.secondarySections.forEach(section => {
        if (section.slug) {
          this.sectionIdMap[section.slug] = section.id;
          // Also store the title for the selected section
          if (section.slug === this.selectedSectionSlug) {
            this.selectedSectionTitle = section.title;
          }
        }
      });
    },
    
    submitThread() {
      if (!this.isFormValid) {
        return;
      }
      
      this.submitting = true;
      
      const threadData = {
        title: this.threadTitle,
        body: this.threadContent,
        forum_tid: this.selectedSectionId
      };
      
      axios.post('/forum/api/thread/create', threadData)
        .then(response => {
          this.submitting = false;
          
          // Emit event to update user activity widget
          this.$root.$emit('userActivityUpdated', 'thread');
          
          // Navigate to the newly created thread
          this.$router.push({ 
            name: 'ForumThread', 
            params: { nid: response.data.nid } 
          });
        })
        .catch(error => {
          console.error('Error creating thread:', error);
          this.error = 'Error al crear el hilo. Por favor, inténtalo de nuevo.';
          this.submitting = false;
        });
    },
    
    cancelThread() {
      // Go back to the previous page
      this.$router.go(-1);
    }
  }
}
</script>

<style scoped>
.forum-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  color: var(--color-text-primary);
}

.forum-layout {
  display: flex;
  gap: 20px;
}

.main-column {
  flex: 3;
  background-color: var(--color-surface);
  border-radius: 15px;
  padding: 20px;
}

.side-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--color-outline);
}

.form-header h1 {
  color: var(--color-text-primary);
}

.thread-form-container {
  padding: 20px 0;
}

.form-group {
  margin-bottom: 25px;
  width: 100%;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.form-control {
  width: 100%;
  max-width: 100%;
  padding: 12px;
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  font-family: inherit;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

select.form-control {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24'%3E%3Cpath fill='%23666' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
}

textarea.form-control {
  display: none;
}

.form-control:focus {
  border-color: var(--color-highlight);
  outline: none;
}

.form-control::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.7;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
}

.cancel-btn, .submit-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.cancel-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
}

.cancel-btn:hover {
  background-color: var(--color-outline);
}

.submit-btn {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

.submit-btn:hover:not(:disabled) {
  background-color: var(--color-highlight-dark, #1976d2);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.forum-guidelines, .formatting-tips {
  background-color: var(--color-surface);
  border-radius: 15px;
  padding: 20px;
}

.forum-guidelines h3, .formatting-tips h3 {
  color: var(--color-text-primary);
}

.guidelines-content, .tips-content {
  margin-top: 15px;
  color: var(--color-text-primary);
}

.guidelines-content p, .tips-content p {
  color: var(--color-text-primary);
}

.guidelines-content ul, .tips-content ul {
  padding-left: 20px;
  margin: 10px 0;
}

.guidelines-content li, .tips-content li {
  margin-bottom: 8px;
  color: var(--color-text-primary);
}

.rules-link {
  display: inline-block;
  margin-top: 10px;
  color: var(--color-highlight);
  text-decoration: none;
}

.rules-link:hover {
  text-decoration: underline;
}

/* Loading and Error Messages */
.loading-message,
.error-message {
  padding: 20px;
  background-color: var(--color-surface);
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
  color: var(--color-text-primary);
}

.error-message {
  border-left: 4px solid #f44336;
  color: #f44336;
}

/* Responsive Design */
@media (max-width: 768px) {
  .forum-layout {
    flex-direction: column;
  }
  
  .main-column,
  .side-column {
    flex: 1;
    width: 100%;
  }
  
  .side-column {
    margin-top: 20px;
  }
}
</style> 